import { useGetSpaceCalendar } from '@cocoonspace/sdk-js/domains/availabilities/availabilities'
import type { SpaceCalendarEntry } from '@cocoonspace/sdk-js/types/spaceCalendarEntry'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { useTranslation } from 'next-i18next'
import { Button } from '~/components/ui/button'
import { Separator } from '~/components/ui/separator'
import { Skeleton } from '~/components/ui/skeleton'
import { useBookingVisitStore } from '../space-visit.store'

const { getTimeAsMinutes } = dateTimeUtils.utils

const getSlotsAsAmPm = (slots: SpaceCalendarEntry[]) => {
	return slots
		.filter((slot) => slot.available)
		.reduce(
			(acc, item) => {
				const midDayAsMin = getTimeAsMinutes('12:00')
				const hourAsMin = getTimeAsMinutes(item.hour)

				hourAsMin < midDayAsMin ? acc.am.push(item) : acc.pm.push(item)

				return acc
			},
			{ am: [], pm: [] } as {
				am: SpaceCalendarEntry[]
				pm: SpaceCalendarEntry[]
			},
		)
}

const BtnList = ({
	slots,
	isLoading,
	label,
}: {
	label: string
	slots: SpaceCalendarEntry[]
	isLoading: boolean
}) => {
	const { t } = useTranslation()

	const setStartTime = useBookingVisitStore.use.setStartTime()
	const setStep = useBookingVisitStore.use.setStep()

	return (
		<div className='flex flex-col gap-2'>
			<div className='flex items-center gap-2 font-bold text-sm'>
				{label} <Separator className='flex-1' />
			</div>

			<ul className='grid w-full grid-cols-4 gap-2'>
				{isLoading &&
					Array.from({ length: 8 }).map((_, i) => (
						<Skeleton
							key={i}
							className='h-8 w-full'
						/>
					))}

				{!isLoading && !slots.length && (
					<div className='flex h-10 items-center justify-center text-sm sm:col-span-2 md:col-span-4'>
						{t('space:visit.dialog.startTime.noAvailableSlotsAmPm')}
					</div>
				)}

				{!isLoading &&
					slots.map((slot) => (
						<Button
							key={slot.hour}
							variant='outline'
							size='sm'
							className='rounded-md font-normal hover:bg-primary hover:text-primary-foreground'
							onClick={() => {
								setStartTime(slot.hour)
								setStep('payment')
							}}
						>
							{slot.hour}
						</Button>
					))}
			</ul>
		</div>
	)
}

export const HourStep = ({
	spaceId,
}: {
	spaceId: string
}) => {
	const { t } = useTranslation()

	const day = useBookingVisitStore.use.day()

	const calendarQuery = useGetSpaceCalendar(
		spaceId,
		{ day, type: 'visit' },
		{ query: { select: (data) => getSlotsAsAmPm(data.data) } },
	)

	return (
		<div className='flex flex-col gap-4'>
			<BtnList
				label={t('space:visit.dialog.startTime.am')}
				slots={calendarQuery.data?.am ?? []}
				isLoading={calendarQuery.isLoading}
			/>

			<BtnList
				label={t('space:visit.dialog.startTime.pm')}
				slots={calendarQuery.data?.pm ?? []}
				isLoading={calendarQuery.isLoading}
			/>
		</div>
	)
}
